import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, RotateCcw } from "lucide-react";
import clsx from "clsx";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import { buildTrayRoster } from "./buildTrayRoster";
import type { TrayListProps } from "./types";

export const TrayList = ({
  trays,
  pointsPerMetre,
  formatMetres,
  areTraysShown,
  onToggleTrays,
  onHighlightType,
  drawnTypes,
  onSetCarriesCable,
  isOpen,
  onOpenChange,
}: TrayListProps) => {
  const { t } = useTranslation();

  // Held in a ref: the panel rebuilds this callback on every edit, and the
  // unmount cleanup below must not re-run for a new arrow.
  const highlightRef = useRef(onHighlightType);
  highlightRef.current = onHighlightType;

  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });

  const roster = useMemo(
    () => buildTrayRoster({ trays, pointsPerMetre }),
    [pointsPerMetre, trays],
  );

  useEffect(() => () => highlightRef.current(null), []);

  if (trays.length === 0) return null;

  return (
    <PanelSection
      tone="tray"
      title={t("smartSurveyor.cablesBeta.traysTitle")}
      count={roster.length}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
      action={
        // The layer's own section owns whether the layer is drawn. One control,
        // where the thing it controls is described — not a toggle bolted onto
        // the chrome, which is what "Cables drawn" had become.
        <button
          type="button"
          aria-pressed={areTraysShown}
          onClick={() => onToggleTrays(!areTraysShown)}
          title={t(
            areTraysShown
              ? "smartSurveyor.cablesBeta.traysHide"
              : "smartSurveyor.cablesBeta.traysShow",
          )}
          className={clsx(
            "mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-md border",
            areTraysShown
              ? "border-slate-300 bg-white text-slate-700"
              : "border-transparent text-slate-400 hover:border-slate-300 hover:bg-white",
          )}
        >
          {areTraysShown ? <Eye size={17} /> : <EyeOff size={17} />}
        </button>
      }
    >
      {!areTraysShown && (
        <p className="mb-1.5 text-[13px] text-slate-500">
          {t("smartSurveyor.cablesBeta.traysHidden")}
        </p>
      )}
      {/* WHICH drawn types cable may ride, and his say over it.
          The vocabulary reads the label — right on most plans, and silent when
          it is wrong: a tray he drew and named "Trasse EG" carried nothing and
          said nothing about it. So every drawn type is listed with what it is
          doing, and one click changes it. Recalculating is what makes it count,
          and the row above turns amber to say so. */}
      {drawnTypes && drawnTypes.length > 0 && onSetCarriesCable && (
        <ul
          data-testid="drawn-route-types"
          className="mb-2 space-y-0.5 border-b border-slate-200 pb-2"
        >
          {drawnTypes.map((entry) => (
            <li
              key={entry.routeTypeId}
              data-testid={`route-type-${entry.routeTypeId}`}
              onMouseEnter={() =>
                entry.carriesCable && areTraysShown
                  ? onHighlightType(entry.label)
                  : undefined
              }
              onMouseLeave={() => onHighlightType(null)}
              className="flex items-center gap-2 rounded px-1 py-1 hover:bg-slate-50"
            >
              <span className="min-w-0 flex-1 truncate text-[14px] text-slate-800">
                {entry.label || t("smartSurveyor.cablesBeta.trayTypeUnnamed")}
              </span>
              <span className="flex-none text-[13px] tabular-nums text-slate-500">
                {t("smartSurveyor.cablesBeta.trayDrawn", {
                  metres: formatMetres(entry.metres),
                })}
              </span>
              {/* Two answers, and the current one is pressed. Not a checkbox:
                  "carries cable" and "does not" are both real answers about a
                  drawn line, and an unticked box reads as "not decided yet". */}
              <button
                type="button"
                aria-pressed={entry.carriesCable}
                onClick={() =>
                  onSetCarriesCable(entry.routeTypeId, !entry.carriesCable)
                }
                title={t(
                  entry.carriesCable
                    ? "smartSurveyor.cablesBeta.trayTypeStopCarrying"
                    : "smartSurveyor.cablesBeta.trayTypeStartCarrying",
                )}
                className={clsx(
                  "flex h-8 flex-none items-center gap-1 rounded-md border px-2 text-[13px] font-semibold",
                  entry.carriesCable
                    ? "border-slate-300 bg-slate-100 text-slate-800 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    : "border-transparent text-slate-400 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700",
                )}
              >
                {t(
                  entry.carriesCable
                    ? "smartSurveyor.cablesBeta.trayTypeCarries"
                    : "smartSurveyor.cablesBeta.trayTypeCarriesNot",
                )}
              </button>
              {/* Only where he overruled it, so the row says whose answer it is
                  — and gives him the way back to ours. */}
              {entry.isHisChoice && (
                <button
                  type="button"
                  onClick={() => onSetCarriesCable(entry.routeTypeId, null)}
                  title={t("smartSurveyor.cablesBeta.trayTypeResetChoice")}
                  aria-label={t("smartSurveyor.cablesBeta.trayTypeResetChoice")}
                  className="grid h-8 w-8 flex-none place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <RotateCcw size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* The product is named HERE, never on the drawing: four labels repeated
          down every tray would bury a sheet that already carries 83 cables. */}
      <ul className="space-y-0.5">
        {roster.map((entry) => (
          <li
            key={entry.type}
            data-testid={`tray-type-${entry.type || "unnamed"}`}
            onMouseEnter={() => areTraysShown && onHighlightType(entry.type)}
            onMouseLeave={() => onHighlightType(null)}
            className="flex items-center gap-2 rounded px-1 py-1 hover:bg-slate-50"
          >
            <span
              aria-hidden
              className="h-1.5 w-6 flex-none rounded-full bg-slate-400"
            />
            <span className="min-w-0 flex-1 truncate text-[14px] text-slate-800">
              {entry.type || t("smartSurveyor.cablesBeta.trayTypeUnnamed")}
            </span>
            <span className="flex-none text-[13px] tabular-nums text-slate-600">
              {t("smartSurveyor.cablesBeta.trayDrawn", {
                metres: formatMetres(entry.metres),
              })}
            </span>
          </li>
        ))}
      </ul>
    </PanelSection>
  );
};
